import {AxiosError} from "axios";
import {useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {MTAApi, PostfixMapType} from '../api/admin/generated';
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface MTAMapPopupProps {
    editType? : PostfixMapType
    editName? : string
    onClose : OnClose
}

export const MTAMapPopup = ({editType, editName, onClose} : MTAMapPopupProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The subject
     */
    const [mapType, setMapType] = useState<PostfixMapType>(editType??PostfixMapType.Hash);
    /**
     * The name of the map
     */
    const [name, setName] = useState<string>(editName??"");
    /**
     * The map content
     */
    const [content, setContent] = useState<string>("");
   /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while saving the map to show a spinner on the download button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        if (editType === undefined || editName === undefined) {
            return;
        }

        logger.debug("Load content")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetMapContent(mapType, name)
            .then(response => {
                setContent(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting map content", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [editName,
        editType,
        mapType,
        name,
        navigate,
        setSpinnerStateCounter,
        t
    ]);

    const addMap = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSaving(true);

        mtaApi.mTAControllerCreateMap(mapType, name).then(() => {
            setMapContent()
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error adding map", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSaving(false);
        });
    }

    const setMapContent = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSaving(true);

        mtaApi.mTAControllerSetMapContent(mapType, name, content).then(() => {
            onClose(DialogCloseCause.Succes);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting map content", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const save = () =>
    {
        if (editName === undefined) {
            addMap();
        }
        else {
            setMapContent();
        }
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{editName !== undefined ? t("mta-maps.edit-map") :  t("mta-maps.add-map")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                 tooltipPlacement={"bottom"}/>

                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control type="text" value={name}
                                              onChange={e => setName(e.target.value)}
                                              disabled={editType !== undefined || editName !== undefined}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t("mta.type")}</Form.Label>
                                <Form.Select size="sm"
                                             aria-label="Map type"
                                             value={mapType}
                                             disabled={editType !== undefined || editName !== undefined}
                                             onChange={(e) => setMapType(Utils.stringToEnum(PostfixMapType, e.target.value as PostfixMapType))}>
                                    <option value="BTREE">Btree</option>
                                    <option value="CIDR">CIDR</option>
                                    <option value="HASH">Hash</option>
                                    <option value="PCRE">PCRE</option>
                                    <option value="REGEXP">RegEx</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t("mta.content")}</Form.Label>
                                <Form.Control
                                    className="font-monospace white-space-pre"
                                    as="textarea"
                                    onChange={e => setContent(e.target.value)}
                                    value={content}
                                    rows={10}
                                    autoFocus
                                />
                            </Form.Group>
                        </Form>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={save}
                        disabled={StringUtils.isBlank(name) || spinnerStateCounter > 0}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
